/**
 * `combat.quick_log_unreviewed` (#259, 07 §13.9.2) — an INFO advisory that
 * fires when a `combat_log`-source bundle remains open (still has un-ratified
 * draft members) for 30+ real-world days. Prompts the GM to sweep stale
 * encounter drafts during async cleanup.
 *
 * Severity: `info` — lowest confidence tier; collapsed by default. A GM may
 * override to `off` if they prefer not to be reminded. Never `blocking_error`
 * (no structural invariant at stake — just a hygiene nudge).
 *
 * No entity_ids / fact_ids: a quick-logged encounter may have no named-PC
 * facts yet (pure fodder). The advisory is bundle-scoped, not fact-scoped.
 * Content-addressed on `(rule, [], [])` per bundle — stable across runs as
 * long as the bundle id is the sole differentiator in `params`.
 *
 * `ctx.now_ms` is injectable for deterministic test clocks; defaults to
 * `Date.now()` so production never needs to supply it.
 */
import type { RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const QUICK_LOG_UNREVIEWED_RULE = 'combat.quick_log_unreviewed';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function quickLogUnreviewed(ctx: AuditContext): RawAdvisory[] {
  const registry = ctx.bundles;
  if (!registry) return [];

  const nowMs = ctx.now_ms ?? Date.now();
  const out: RawAdvisory[] = [];

  for (const entry of Object.values(registry)) {
    if (entry.state !== 'open') continue;
    if (entry.metadata?.source !== 'combat_log') continue;
    const age = nowMs - (entry.metadata.created_hlc.wall_ms ?? 0);
    if (age < THIRTY_DAYS_MS) continue;

    out.push({
      severity: 'info',
      rule: QUICK_LOG_UNREVIEWED_RULE,
      message_key: 'auditor.combat.quick_log_unreviewed',
      params: { bundle_id: entry.bundle_id },
      fact_ids: [],
      entity_ids: [],
    });
  }

  return out;
}
